/**
 * Sound effects using Web Audio API
 * Creates simple synthesized sounds without external files
 */

let audioContext = null;

function getAudioContext() {
  if (!audioContext && typeof window !== 'undefined' && window.AudioContext) {
    audioContext = new (window.AudioContext || window.webkitAudioContext)();
  }
  return audioContext;
}

/**
 * Play a tone
 * @param {number} frequency - Frequency in Hz
 * @param {number} duration - Duration in seconds
 * @param {string} type - Waveform type ('sine', 'square', 'triangle', 'sawtooth')
 */
function playTone(frequency, duration, type = 'sine') {
  const ctx = getAudioContext();
  if (!ctx) return;

  const oscillator = ctx.createOscillator();
  const gainNode = ctx.createGain();

  oscillator.connect(gainNode);
  gainNode.connect(ctx.destination);

  oscillator.frequency.value = frequency;
  oscillator.type = type;

  gainNode.gain.setValueAtTime(0.3, ctx.currentTime);
  gainNode.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + duration);

  oscillator.start(ctx.currentTime);
  oscillator.stop(ctx.currentTime + duration);
}

/**
 * Sound effects
 */
export const sounds = {
  /**
   * Play correct answer sound (happy ascending notes)
   */
  correct: () => {
    playTone(523.25, 0.1); // C5
    setTimeout(() => playTone(659.25, 0.1), 100); // E5
    setTimeout(() => playTone(783.99, 0.2), 200); // G5
  },

  /**
   * Play wrong answer sound (descending notes)
   */
  wrong: () => {
    playTone(392, 0.15, 'square'); // G4
    setTimeout(() => playTone(329.63, 0.3, 'square'), 150); // E4
  },

  /**
   * Play level up sound (triumphant)
   */
  levelUp: () => {
    playTone(523.25, 0.1); // C5
    setTimeout(() => playTone(659.25, 0.1), 100); // E5
    setTimeout(() => playTone(783.99, 0.1), 200); // G5
    setTimeout(() => playTone(1046.5, 0.3), 300); // C6
  },

  /**
   * Play achievement sound
   */
  achievement: () => {
    playTone(659.25, 0.1); // E5
    setTimeout(() => playTone(783.99, 0.1), 80); // G5
    setTimeout(() => playTone(1046.5, 0.1), 160); // C6
    setTimeout(() => playTone(1318.51, 0.2), 240); // E6
  },

  /**
   * Play click sound
   */
  click: () => {
    playTone(800, 0.05, 'square');
  },

  /**
   * Play game over sound
   */
  gameOver: () => {
    playTone(392, 0.2); // G4
    setTimeout(() => playTone(349.23, 0.2), 200); // F4
    setTimeout(() => playTone(293.66, 0.2), 400); // D4
    setTimeout(() => playTone(261.63, 0.4), 600); // C4
  },
};

/**
 * Play a sound effect if sounds are enabled
 * @param {string} soundName - Name of the sound to play
 * @param {boolean} soundEnabled - Whether sounds are enabled
 */
export function playSound(soundName, soundEnabled = true) {
  if (!soundEnabled || !sounds[soundName]) return;

  try {
    sounds[soundName]();
  } catch (error) {
    console.error('Error playing sound:', error);
  }
}

/**
 * Initialize audio context on user interaction (required by browsers)
 */
export function initializeAudio() {
  const ctx = getAudioContext();
  if (ctx && ctx.state === 'suspended') {
    ctx.resume();
  }
}
